import React, { useMemo } from 'react';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import { useRoundCache } from '../../../hooks/useRoundCache';
import {
  ActionRow,
  CounterRow,
  MissionHeader,
  MissionRow,
  OptionCard,
  OptionsGroup,
  ScoreSummary,
} from './styles';
interface IModalityRef {
  evaluation_type: 'binary' | 'subjective';
  bonus_time_seconds: number;
  bonus_points: number;
  penalty_per_occurrence: number;
  max_score?: number;
}

interface IEvaluationSubmitData {
  mission_scores: { mission_order: number; score: number }[];
  touch_count?: number;
  infraction_count?: number;
}

interface IPainterRobotFormProps {
  roundId: string;
  modalityRef: IModalityRef;
  timerSeconds: number;
  onSubmit: (data: IEvaluationSubmitData) => Promise<void>;
  isSubmitting: boolean;
}

const MISSIONS = [1, 2, 3, 4, 5];
const OPTIONS = [
  { label: 'Execução perfeita', points: 20 },
  { label: 'Boa', points: 15 },
  { label: 'Parcial', points: 10 },
  { label: 'Falha', points: 5 },
  { label: 'Não Realizada', points: 0 },
];

export const RoboPintorForm: React.FC<IPainterRobotFormProps> = ({
  roundId,
  modalityRef,
  timerSeconds,
  onSubmit,
  isSubmitting,
}) => {
  const [state, setState, clearCache] = useRoundCache(
    `round_evaluate_${roundId}_robopintor`,
    {
      missions: {} as Record<number, number>,
      infraction_count: 0,
    },
  );

  const handleSelect = (mission: number, points: number) => {
    setState(prev => ({
      ...prev,
      missions: { ...prev.missions, [mission]: points },
    }));
  };

  const handleInfractionChange = (delta: number) => {
    setState(prev => ({
      ...prev,
      infraction_count: Math.max(0, prev.infraction_count + delta),
    }));
  };

  const missionSum = useMemo(
    () => Object.values(state.missions).reduce((acc, curr) => acc + curr, 0),
    [state.missions],
  );

  const maxScore = modalityRef.max_score ?? MISSIONS.length * 20;
  const allAnswered = MISSIONS.every(m => state.missions[m] !== undefined);
  const bonusApplied =
    missionSum === maxScore &&
    timerSeconds > 0 &&
    timerSeconds < modalityRef.bonus_time_seconds;
  const bonusScore = bonusApplied ? modalityRef.bonus_points : 0;
  const penaltyScore =
    state.infraction_count * modalityRef.penalty_per_occurrence;
  const totalScore = Math.max(0, missionSum + bonusScore - penaltyScore);

  const handleSubmit = async () => {
    const mission_scores = MISSIONS.map(m => ({
      mission_order: m,
      score: state.missions[m] ?? 0,
    }));
    await onSubmit({
      mission_scores,
      infraction_count: state.infraction_count,
    });
    clearCache();
  };

  return (
    <>
      {MISSIONS.map(mission => {
        const selectedPoints = state.missions[mission];
        return (
          <MissionRow key={mission}>
            <MissionHeader>
              <h4>Missão {mission}</h4>
              <span>
                {selectedPoints !== undefined ? `${selectedPoints}pts` : '0pts'}
              </span>
            </MissionHeader>
            <OptionsGroup>
              {OPTIONS.map(opt => (
                <OptionCard
                  key={opt.label}
                  type="button"
                  $selected={selectedPoints === opt.points}
                  $isZero={opt.points === 0}
                  onClick={() => handleSelect(mission, opt.points)}
                >
                  <span className="label">{opt.label}</span>
                  <span className="points">{opt.points} pts</span>
                </OptionCard>
              ))}
            </OptionsGroup>
          </MissionRow>
        );
      })}

      <CounterRow>
        <span className="label">
          Infrações (-{modalityRef.penalty_per_occurrence} pts cada)
        </span>
        <div className="controls">
          <button
            type="button"
            onClick={() => handleInfractionChange(-1)}
            disabled={state.infraction_count === 0}
          >
            -
          </button>
          <span className="value">{state.infraction_count}</span>
          <button
            type="button"
            className="orange"
            onClick={() => handleInfractionChange(1)}
          >
            +
          </button>
        </div>
      </CounterRow>

      <ScoreSummary>
        <div className="box">
          <span className="label">Missões</span>
          <span className="value">
            {missionSum}/{maxScore}
          </span>
        </div>
        <div className="box">
          <span className="label">Bônus</span>
          <span className="value bonus">+{bonusScore}</span>
        </div>
        <div className="box">
          <span className="label">Penalidade</span>
          <span className="value penalty">-{penaltyScore}</span>
        </div>
        <div className="box">
          <span className="label">Total da rodada</span>
          <span className="value total">{totalScore}</span>
        </div>
      </ScoreSummary>

      <ActionRow>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', width: '100%' }}>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || timerSeconds === 0 || !allAnswered}
          >
            {isSubmitting ? 'Registrando...' : 'Registrar avaliação'}
          </PrimaryButton>
          {timerSeconds === 0 && (
            <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
              Inicie o cronômetro antes de registrar
            </span>
          )}
          {timerSeconds > 0 && !allAnswered && (
            <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
              Preencha todas as missões para continuar
            </span>
          )}
        </div>
      </ActionRow>
    </>
  );
};
